import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export interface ColumnPickerHandle {
  columns: { id: string; label: string }[];
  visibility: Record<string, boolean>;
  onToggle: (id: string) => void;
}

interface ContextShape {
  current: ColumnPickerHandle | null;
  setCurrent: (h: ColumnPickerHandle | null) => void;
}

const Ctx = createContext<ContextShape | null>(null);

export function ColumnPickerProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<ColumnPickerHandle | null>(null);
  return <Ctx.Provider value={{ current, setCurrent }}>{children}</Ctx.Provider>;
}

/** Called by the currently-mounted table to make its column-visibility API available to HeaderBar. */
export function useRegisterColumnPicker(handle: ColumnPickerHandle | null) {
  const ctx = useContext(Ctx);
  useEffect(() => {
    if (!ctx) return;
    ctx.setCurrent(handle);
    return () => ctx.setCurrent(null);
  }, [ctx, handle]);
}

/** Read the active table's column-visibility handle. Returns null when no table is mounted under a provider. */
export function useColumnPicker(): ColumnPickerHandle | null {
  return useContext(Ctx)?.current ?? null;
}
